import {
    SeriesInstanceUid,
    seriesInstanceUidOf,
    toSeriesInstanceUid,
} from '~/domain/dicom/uids';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useRegistryLifecycleVersion } from '~/hooks/target/useActiveEditTarget';
import { useFusionVersion } from '~/hooks/environment/useFusionVersion';
import PetColorwashControl from './PetColorwashControl';
import PetMipToggleButton from './PetMipToggleButton';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { petColorMapById } from '~/domain/viewport/fusionBlend';
import { useImageStreamProgress } from '~/hooks/image/useImageStreamProgress';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import ThemeProvider from '~/theme/ThemeProvider';
import { RenderAdapter } from '~/viewportGrid/adapters/types';
import { focusRowOnPetPoint } from '../focusRowOnPetPoint';
import { fusedPairOf } from '~/engine/view/layers/fusedImageDisplay';
import { petImageOf, petPointInRowFrame } from '../petImageOf';
import { usePlaneViewOf } from '../usePlaneViewOf';
import { usePlaneRedrawTick } from '~/hooks/viewer/annotationOverlay';

interface TilePetColorwashMountProps {
    renderer: RenderAdapter;
    /** The images the tile leads, as series instance uids. */
    ledImages: ReadonlyArray<SeriesInstanceUid>;
}

/**
 * Mounts the PET colorwash control on the tile that leads the PET a row
 * draws, its own image or a fused one, and on no other tile of the row:
 * the control sits on a tile that shows the PET it windows, and every tile
 * drawing that series follows the window. The ramp pictured is the row's
 * own, and the "go to the maximum" button centres the row's tiles on the
 * PET maximum, carried into the row's frame when the user asks; the PET MIP
 * toggle opens the rotatable projection over this row. The control is
 * drawn under the app theme, since the grid's console chrome has no
 * overlay glass. Renders nothing for non-engine renderers (the mock
 * adapters).
 */
/** The view-manager event a tile's own fusion is announced by. */
const FUSION_EVENTS: readonly string[] = ['fusedImageDisplayChanged'];

export default function TilePetColorwashMount({
    renderer,
    ledImages,
}: TilePetColorwashMountProps) {
    const view = usePlaneViewOf(renderer);
    const rowSeriesUid = seriesInstanceUidOf(
        view?.environment.image?.parent.seriesUid
    );
    // A fuse or clear re-mints nothing but changes what the Environment
    // draws; the lifecycle and the fusion writes are what re-read it.
    useRegistryLifecycleVersion();
    useFusionVersion(rowSeriesUid);
    const viewManager = useRecoilValue(viewManagerState);
    // A tile's own fusion is set on its view, which announces it so.
    usePlaneRedrawTick(viewManager, FUSION_EVENTS);
    // A tile with its own fusion draws its own partner, not the Environment's.
    const pair = view ? fusedPairOf(view) : undefined;
    const petImage = pair ? petImageOf(pair) : undefined;
    // A PET that loads slice by slice changes in place; its progress record
    // is what re-renders the tile as slices land.
    useImageStreamProgress(petImage);
    const hasMeasuredRange = petImage?.hasMeasuredIntensityRange ?? false;
    // Finding the maximum scans the whole PET volume, so it runs once per
    // PET, and not before the PET has a measured range: until then the
    // volume has no maximum. The point is in the PET's own frame, which no
    // registration moves.
    const maxInPetFrame = useMemo(
        () =>
            petImage && hasMeasuredRange
                ? petImage.findMaxIntensityPosition()
                : null,
        [petImage, hasMeasuredRange]
    );
    if (
        !view ||
        !pair ||
        !petImage ||
        !ledImages.some(
            (uid) => uid === toSeriesInstanceUid(petImage.seriesUid)
        )
    ) {
        return null;
    }
    const environment = pair;
    // A registration edit rewrites its matrix in place and notifies nothing
    // here, so the point is carried into the row's frame at the click.
    const navigateToMax =
        maxInPetFrame && petPointInRowFrame(environment, maxInPetFrame)
            ? () => {
                  focusRowOnPetPoint(
                      getDatasetEnvironmentRegistry(),
                      viewManager,
                      environment,
                      maxInPetFrame
                  );
              }
            : undefined;
    return (
        <Box
            data-testid="tile-pet-colorwash"
            sx={{ position: 'absolute', right: 35, top: '10%' }}
        >
            <ThemeProvider>
                <PetColorwashControl
                    petImage={petImage}
                    colorMap={petColorMapById(
                        view.viewFusion?.blend.petColorMapId ??
                            view.environment.petFusionColorMapTypeIdx
                    )}
                    navigateToMax={navigateToMax}
                    headerAction={
                        // The projection is of the PET the row image's
                        // Environment draws, not a tile's own partner.
                        rowSeriesUid !== undefined &&
                        petImageOf(view.environment) === petImage ? (
                            <PetMipToggleButton rowSeriesUid={rowSeriesUid} />
                        ) : undefined
                    }
                />
            </ThemeProvider>
        </Box>
    );
}
